import { AuthUseCaseInterface } from 'application/usecases/AuthUseCase';
import { ChatUseCase } from 'application/usecases/ChatUseCase';
import { NotifyUseCaseInterface } from 'application/usecases/NotifyUseCase';
import { UserStorageUseCaseInterface } from 'application/usecases/UserStorageUseCase';
import { UserUseCaseInterface } from 'application/usecases/UserUseCase';
import { ChatCount } from 'domain/entities/ChatEntity';
import { UserToken } from 'domain/entities/UserEntity';
import { ENV } from 'infrastructure/config/CONSTANTS';
import {
    completeFetching,
    showConfirm,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import {
    closeSocket,
    connecSocket,
    listenSocketEvent,
    pingSocket,
} from 'presentation/redux/features/app/socketSlice';
import { logout } from 'presentation/redux/features/authen/authenSlice';
import {
    realoadAndCountMessage,
    resetMessageCount,
    setChatRoom,
    setMessageConnectSocket,
    setUnreadMessageCount,
} from 'presentation/redux/features/counter/chatCounterSlice';
import {
    reCountNotify,
    resetNotifyCount,
    setNotifyConnectSocket,
    setUnreadNotifyCount,
} from 'presentation/redux/features/counter/notifyCounterSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import { AUTH_ROUTES, PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { commonHelpers } from 'utilities/helpers';
import { MenuHeaderView } from './MenuHeaderView';

interface props {
    userStorageUseCase: UserStorageUseCaseInterface;
    children?: string | JSX.Element | JSX.Element[];
    authUseCase: AuthUseCaseInterface;
    userUseCase: UserUseCaseInterface;
    chatUseCase: ChatUseCase;
    notifyUseCase: NotifyUseCaseInterface;
}

const useUnload = (fn: any) => {
    const cb = React.useRef(fn);

    React.useEffect(() => {
        const onUnload = cb.current;
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
        };
    }, [cb]);
};

export const MenuHeaderContainer = ({
    children,
    userStorageUseCase,
    authUseCase,
    chatUseCase,
    notifyUseCase,
}: props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const [userLocal, setUserLocal] = useState<UserToken | null | undefined>();
    const [chosenItem, setChosenItem] = useState<string[]>([]);
    const [isRendering, setIsRendering] = useState<boolean>(false);
    const appUser = useAppSelector((state) => state.user);
    const appSocketSlice = useAppSelector((state) => state.socketSlice);
    const notifyCounterSlice = useAppSelector((state) => state.notifyCounterSlice);
    const chatCounterSlice = useAppSelector((state) => state.chatCounterSlice);
    useUnload(() => {
        dispatch(closeSocket());
    });
    useEffect(() => {
        if (notifyCounterSlice.reCount) {
            const user = userStorageUseCase.getUserInfo();
            if (user) {
                getCountNotify();
            }
        }
    }, [notifyCounterSlice.reCount]);
    useEffect(() => {
        if (chatCounterSlice.reCount) {
            const user = userStorageUseCase.getUserInfo();
            if (user) {
                getChatCount();
            }
        }
    }, [chatCounterSlice.reCount]);

    useEffect(() => {
        initiateMenu();
    }, [location.pathname]);

    const getChatCount = async () => {
        try {
            const response = await chatUseCase.getUnreadMessage();
            if (response && response?.data?.length > 0) {
                const userCount = response.data.find(
                    (item: ChatCount) => item?.chatRoomId === chatCounterSlice.chatRoom?.chatRoomId,
                );
                dispatch(setUnreadMessageCount(userCount?.chatMessagesCount ?? 0));
            } else {
                dispatch(setUnreadMessageCount(0));
            }
        } catch (e) {
            // console.log(e);
        }
    };

    const getCountNotify = async () => {
        try {
            const response = await notifyUseCase.getCountNotify();
            if (response.data) {
                dispatch(setUnreadNotifyCount(response.data.unread));
            }
        } catch (error: any) {
            // console.log(error)
        }
    };

    useEffect(() => {
        const user = userStorageUseCase.getUserInfo();
        setUserLocal(user);
        setIsRendering(true);
        if (user) {
            load_connected_chat_room(user);
            //ping socket every 5 minutes to avoid socket disconnect by aws
            const interval = setInterval(
                () => {
                    dispatch(pingSocket(user));
                },
                5 * 60 * 1000,
            );
            return () => {
                appSocketSlice.disconnect();
                clearInterval(interval);
            };
        }
    }, []);

    const load_connected_chat_room = async (user: UserToken) => {
        try {
            const chatRoomInfo = await chatUseCase.getChatRoom();
            dispatch(setChatRoom(chatRoomInfo.data));
            listenNotify(user);
        } catch (e) {
            listenNotify(user);
        }
    };

    const listenNotify = (user: UserToken) => {
        const socketOpenEvent = {
            url: ENV.SOCKET_BASE,
            user: user,
            additionaOpenEvent: () => {
                if (location.pathname.indexOf(PRIVATE_ROUTES.SUB.CU_012_01) != -1) {
                    dispatch(setMessageConnectSocket(true));
                } else {
                    dispatch(setMessageConnectSocket(false));
                }
                dispatch(setNotifyConnectSocket());
            },
        };
        //open socket connection
        dispatch(connecSocket(socketOpenEvent));
        // listen socket event
        const socketListenEvent = {
            name: 'message',
            callback: (event: any) => {
                const response = JSON.parse(event.data);
                if (response?.action === 'pushNotification') {
                    dispatch(reCountNotify());
                }
                if (
                    response?.action === 'sendMessage' ||
                    response?.action === 'updateMessageStatus'
                ) {
                    if (
                        response?.action === 'sendMessage' ||
                        response?.action === 'updateMessageStatus'
                    ) {
                        dispatch(realoadAndCountMessage(response));
                    }
                }
            },
        };
        dispatch(listenSocketEvent(socketListenEvent));
    };

    const logoutApp = async () => {
        const confirmProps = commonHelpers.getConfirmProps('LOGOUT_APP', {
            onOk: async () => {
                dispatch(startFetching());
                try {
                    if (appUser?.isSignedIn) {
                        await authUseCase.logout();
                    }
                } catch (e) {
                    // console.log(e);
                }
                dispatch(closeSocket());
                dispatch(resetMessageCount());
                dispatch(resetNotifyCount());
                dispatch(logout());
                userStorageUseCase.removeUserInfo();
                dispatch(completeFetching());
                toSigninPage();
            },
        });
        dispatch(showConfirm(confirmProps));
    };

    const toSigninPage = () => {
        navigate(AUTH_ROUTES.PATH + '/' + AUTH_ROUTES.SUB.CU_001_00);
    };

    const toSignupPage = () => {
        navigate(AUTH_ROUTES.PATH + '/' + AUTH_ROUTES.SUB.CU_001_01);
    };

    const selectMenuItem = (value: any) => {
        navigate(PRIVATE_ROUTES.PATH + '/' + value.key);
        setChosenItem(value.key);
    };

    const existMenuKey = (key: string) => {
        // const existInMenu1 = MenuItems1.find((item) => item.key.indexOf(key) > -1);
        // const existInMenu2 = MenuItems2.find((item) => item.key.indexOf(key) > -1);
        const existInMenu1 = false;
        const existInMenu2 = false;
        if (existInMenu1 || existInMenu2) {
            return true;
        }
        return false;
    };

    const initiateMenu = () => {
        const pathName = location.pathname;
        const pathNameArr = pathName.split('/');
        if (pathNameArr.length > 0 && pathNameArr[pathNameArr.length - 1] !== '') {
            if (
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_028_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_029_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_029_02 ||
                pathNameArr[pathNameArr.length - 3] === PRIVATE_ROUTES.SUB.CU_031_01 ||
                pathNameArr[pathNameArr.length - 3] === PRIVATE_ROUTES.SUB.CU_033_01 ||
                pathNameArr[pathNameArr.length - 2] ===
                    PRIVATE_ROUTES.SUB.CU_030_01.split('/')[0] ||
                (pathNameArr.length == 5 && [PRIVATE_ROUTES.SUB.CU_036_01].includes(pathNameArr[2]))
            ) {
                setChosenItem([PRIVATE_ROUTES.SUB.CU_056_01]);
            } else if (
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_049_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_055_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_055_02 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_055_03 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_055_04 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_055_05 ||
                pathNameArr[pathNameArr.length - 2] === PRIVATE_ROUTES.SUB.CU_049_02
            ) {
                setChosenItem([PRIVATE_ROUTES.SUB.CU_055_01]);
            } else if (
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_050_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_010_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_051_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_051_02 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_052_01 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_052_02 ||
                pathNameArr[pathNameArr.length - 1] === PRIVATE_ROUTES.SUB.CU_053_01 ||
                pathNameArr[pathNameArr.length - 2].includes(PRIVATE_ROUTES.SUB.CU_010_02) ||
                pathNameArr[pathNameArr.length - 3].includes(PRIVATE_ROUTES.SUB.CU_051_02) ||
                pathNameArr[pathNameArr.length - 2].includes(PRIVATE_ROUTES.SUB.CU_052_02) ||
                pathNameArr[pathNameArr.length - 2].includes(PRIVATE_ROUTES.SUB.CU_053_02) ||
                pathNameArr[pathNameArr.length - 2].includes(PRIVATE_ROUTES.SUB.CU_058_01)
            ) {
                setChosenItem([PRIVATE_ROUTES.SUB.CU_050_01]);
            } else if (
                pathNameArr.length >= 2 &&
                [
                    PRIVATE_ROUTES.SUB.CU_036_02,
                    PRIVATE_ROUTES.SUB.CU_035_01,
                    PRIVATE_ROUTES.SUB.CU_009_01,
                ].includes(pathNameArr[2])
            ) {
                setChosenItem([PRIVATE_ROUTES.SUB.CU_002_01]);
            } else if (
                (pathNameArr.length > 5 &&
                    [PRIVATE_ROUTES.SUB.CU_036_01].includes(pathNameArr[2])) ||
                pathNameArr[pathNameArr.length - 2].includes(PRIVATE_ROUTES.SUB.CU_002_03)
            ) {
                setChosenItem([PRIVATE_ROUTES.SUB.CU_002_02]);
            } else if (
                pathNameArr.length >= 2 &&
                [PRIVATE_ROUTES.SUB.CU_012_02].includes(pathNameArr[2])
            ) {
                setChosenItem([PRIVATE_ROUTES.SUB.CU_012_01]);
            } else if (existMenuKey(pathNameArr[pathNameArr.length - 1])) {
                setChosenItem([pathNameArr[pathNameArr.length - 1]]);
            } else {
                setChosenItem([])
            }
        }
    };

    return (
        <MenuHeaderView
            selectMenuItem={selectMenuItem}
            userStorageUseCase={userStorageUseCase}
            logoutApp={logoutApp}
            toSigninPage={toSigninPage}
            toSignupPage={toSignupPage}
            chosenItem={chosenItem}
            userLocal={userLocal}
            isRendering={isRendering}
        >
            {children}
        </MenuHeaderView>
    );
};
